import React from 'react';
import {
  // DeleteOutlined,
  EditOutlined,
  LoadingOutlined,
  WarningTwoTone,
} from '@ant-design/icons';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import {
  Button,
  Empty,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnType } from 'antd/es/table';
import type { SortOrder } from 'antd/es/table/interface';
import { EMPTY, PRETTY_DATE_FORMAT } from '@owl-frontend/components';
import type { Claim } from '../../../api/claims/interface';
import OwlLogo from '../../../assets/app-spinner.svg';
import { Locale, SessionContext } from '../../../context/AppProvider';
import { FeatureKeys } from '../../../data/session/interface';
import { ShellRouteDefs } from '../../ClaimOverviewShellContainer/ClaimOverviewLandingContainer';
import { generatePath, RouteDefs } from '../../Routing/Routing';
import styles from '../ClaimsContainer.module.scss';
import { isProcessing } from '../data/logic';
import type { ClaimsHookState } from '../useClaimsHook';

const statusColors: Record<string, string> = {
  done: 'green',
  error: 'red',
  deleted: 'black',
  no_data: 'grey',
};

export const ClaimProcessingStatusComponent: React.FC<{
  claimStatus: string;
}> = ({ claimStatus }) => {
  const { messages } = React.useContext(Locale);

  return (
    <Tag
      className={styles.claimStatus}
      color={
        claimStatus
          ? statusColors[claimStatus.toLowerCase()] || 'default'
          : 'red'
      }
    >
      <Space>
        {isProcessing(claimStatus) && (
          <Spin indicator={<LoadingOutlined spin />} size="small" />
        )}
        {isProcessing(claimStatus)
          ? messages['claims.hub.status.is_processing']
          : messages[`claims.hub.status.${claimStatus.toLowerCase()}`]}
      </Space>
    </Tag>
  );
};

interface Props {
  data: Claim[];
  loading: boolean;
  onSortChange(
    sort: { key: string[]; order: 'ascend' | 'descend' } | null
  ): void;
  onPageChange(data: {
    currentPage: number;
    pageSize: number;
    sort: ClaimsHookState['sort'];
  }): void;
  pagination: {
    currentPage: number;
    pageSize: number;
    total: number;
  };
  setCreateModal(createModal: boolean): void;
  setSelectedCase(
    data:
      | {
          firstName?: string;
          lastName?: string;
          clientDossierId: string;
          notes: string;
          dossierId: string;
          data: {
            claimants: string[];
            insured: string;
          };
          dossierType: string;
        }
      | undefined
  ): void;
  onClearFilter(): void;
}

const ClaimsListComponent: React.FC<Props> = ({
  data,
  loading,
  onSortChange,
  onPageChange,
  pagination,
  setCreateModal,
  setSelectedCase,
  onClearFilter,
}) => {
  const { messages } = React.useContext(Locale);
  const [hoveredRow, setHoveredRow] = React.useState<string | null>(null);
  const { features } = React.useContext(SessionContext);

  const columns = React.useMemo(() => {
    const baseColumns: ColumnType<Claim>[] = [
      {
        key: 'client_dossier_id',
        dataIndex: 'client_dossier_id',
        title: messages['claims.hub.self_service.columns.case_id'],
        fixed: 'left' as const,
        width: 150,
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (client_dossier_id, dossier) => {
          if (dossier.status === 'done') {
            return (
              <Link
                data-testid={`claim-link-${dossier.dossier_id}`}
                data-dd-privacy="allow"
                className={styles.claimIdButton}
                to={{
                  pathname: generatePath(
                    features[FeatureKeys.DemoClaimOverview]
                      ? ShellRouteDefs.ClaimOverviewShell
                      : RouteDefs.ClaimOverview,
                    {
                      dossierId: dossier.dossier_id,
                    }
                  ),
                  search: `?from=${RouteDefs.Claims}`,
                  hash: 'summaries',
                }}
                state={{ from: RouteDefs.Claims }}
              >
                {client_dossier_id}
              </Link>
            );
          }

          return <div data-dd-privacy="allow">{client_dossier_id}</div>;
        },
      },
      {
        key: 'dossier_name',
        dataIndex: 'dossier_name',
        title: messages['claims.hub.self_service.columns.claimant_name'],
        width: 150,
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (value: string) => {
          return (
            <Tooltip title={value}>
              <div
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: '150px',
                }}
              >
                {value ?? EMPTY}
              </div>
            </Tooltip>
          );
        },
      },
      ...(features[FeatureKeys.MultiParticipantClaims]
        ? [
            {
              key: 'insured',
              dataIndex: 'insured',
              title: messages['claims.hub.self_service.columns.insured'],
              width: 150,
              sorter: true,
              sortDirections: [
                'ascend' as const,
                'descend' as const,
                'ascend' as const,
              ],
              render: (value: string, dossier) => {
                return (
                  <Tooltip title={dossier?.data?.insured}>
                    <div
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '150px',
                      }}
                    >
                      {dossier?.data?.insured}
                    </div>
                  </Tooltip>
                );
              },
            },
          ]
        : []),
      {
        key: 'notes',
        dataIndex: 'notes',
        minWidth: 150,
        title: messages['claims.hub.self_service.columns.notes'],
        render: (value: string, dossier) => {
          return dossier?.data?.notes ?? EMPTY;
        },
      },
      {
        key: 'uploaded_by',
        dataIndex: 'assignee',
        title: messages['claims.hub.self_service.columns.uploaded_by'],
        width: 150,
        render: (value: string) => {
          if (!value) {
            return EMPTY;
          }

          return <div data-dd-privacy="allow">{value}</div>;
        },
      },
      {
        key: 'updated_at',
        dataIndex: 'updated_at',
        title: messages['claims.hub.self_service.columns.upload_date'],
        width: 150,
        render: (value: string) => {
          return dayjs(value).format(PRETTY_DATE_FORMAT);
        },
        defaultSortOrder: 'descend' as SortOrder,
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
      },
    ];

    if (features[FeatureKeys.SelfServiceView]) {
      baseColumns.push({
        key: 'status',
        dataIndex: 'status',
        title: messages['claims.hub.self_service.columns.status'],
        align: 'right' as const,
        width: 220,
        render: (value: string, dossier) => {
          if (hoveredRow !== dossier.client_dossier_id) {
            return (
              <Tooltip
                title={
                  value === 'error'
                    ? messages['claims.hub.self_service.error']
                    : ''
                }
                placement="top"
              >
                <Space>
                  {value === 'error' && (
                    <WarningTwoTone twoToneColor={['orange', '#fff']} />
                  )}
                  <ClaimProcessingStatusComponent claimStatus={value} />
                </Space>
              </Tooltip>
            );
          }

          return (
            <Space>
              {value === 'error' && (
                <Tooltip
                  title={
                    value === 'error'
                      ? messages['claims.hub.self_service.error']
                      : ''
                  }
                  placement="top"
                >
                  <WarningTwoTone twoToneColor={['orange', '#fff']} />
                </Tooltip>
              )}
              <Space className={styles.moreActions} size={3}>
                <Button
                  type="default"
                  size="small"
                  onClick={() => {
                    setSelectedCase({
                      dossierType: dossier.dossierType,
                      firstName: dossier.data.first_nm ?? '',
                      lastName: dossier.data.last_nm ?? '',
                      clientDossierId: dossier.client_dossier_id,
                      notes: dossier.data.notes,
                      dossierId: dossier.dossier_id,
                      data: {
                        claimants: dossier.data
                          .claimants as unknown as string[],
                        insured:
                          dossier.data.insured ??
                          (dossier.data.first_nm && dossier.data.last_nm
                            ? `${dossier.data.first_nm} ${dossier.data.last_nm}`
                            : ''),
                      },
                    });
                    setCreateModal(true);
                  }}
                >
                  <EditOutlined />
                  {messages['claims.hub.self_service.update.button']}
                </Button>
              </Space>
            </Space>
          );
        },
      });
    }
    return baseColumns;
  }, [features, hoveredRow, messages, setCreateModal, setSelectedCase]);

  const handleTableChange = React.useCallback(
    (newPagination, _, sorter) => {
      onPageChange({
        currentPage: newPagination.current,
        pageSize: newPagination.pageSize,
        sort: {
          key: sorter.column.dataIndex,
          order: sorter.order,
        },
      });
      if (!sorter.column) {
        onSortChange(null);
        return;
      }
    },
    [onPageChange, onSortChange]
  );

  return (
    <Spin spinning={loading}>
      <Table<Claim>
        rowKey={(record) => {
          let result = record.dossier_id;
          if (record.assignee) {
            result = `${result}--${record.assignee}`;
          }
          if (record.assignee_id) {
            result = `${result}--${record.assignee_id}`;
          }
          return result;
        }}
        className={styles.claimsTable}
        rowClassName={({ status }) =>
          clsx(isProcessing(status) && styles.rowDisabled)
        }
        dataSource={data}
        columns={columns}
        onChange={handleTableChange}
        locale={{
          emptyText: features[FeatureKeys.SelfServiceView] ? (
            <Empty
              image={<OwlLogo className={styles.owlLogo} />}
              className={styles.emptyContainer}
              description={
                <div className={styles.emptyContainerComponents}>
                  <Typography.Title level={4}>
                    {messages['claims.hub.self_service.empty.welcome']}
                  </Typography.Title>
                  <Typography.Text className={styles.emptyMessage}>
                    {messages['claims.hub.self_service.empty.welcome_message']}
                  </Typography.Text>
                  <Button type="primary" onClick={onClearFilter}>
                    {messages['claims.hub.filters.clear']}
                  </Button>
                </div>
              }
            />
          ) : (
            <Empty />
          ),
        }}
        onRow={(dossier) => ({
          onMouseEnter: () => setHoveredRow(dossier.client_dossier_id),
          onMouseLeave: () => setHoveredRow(null),
        })}
        scroll={{
          x: 'max-content',
          y: features[FeatureKeys.SelfServiceView]
            ? 'calc(100vh - 375px)'
            : 'calc(100vh - 325px)',
        }}
        pagination={{
          current: pagination.currentPage,
          pageSize: pagination.pageSize,
          total: pagination.total,
        }}
      />
    </Spin>
  );
};

export default ClaimsListComponent;
